/**
 * Architectural Analysis - Domain Entity  
 * 
 * Represents a comprehensive analysis of monorepo architecture including
 * bounded context relationships, dependency health, and pattern compliance.
 * 
 * Following Eric Evans DDD principles for meta-modeling system architecture.
 */

import { Entity, EntityId } from '@swoft/core';
import { z } from 'zod';
import { v4 as uuidv4 } from 'uuid';

// Value Objects
export const ArchitecturalAnalysisIdSchema = z.string().uuid();

export class ArchitecturalAnalysisId extends EntityId {
  constructor(private readonly value: string) {
    super();
  }

  static generate(): ArchitecturalAnalysisId {
    return new ArchitecturalAnalysisId(uuidv4());
  }

  static fromString(value: string): ArchitecturalAnalysisId {
    return new ArchitecturalAnalysisId(value);
  }

  toString(): string {
    return this.value;
  }
}

export const MonorepoHealthSchema = z.object({
  totalPackages: z.number(),
  totalBoundedContexts: z.number(),
  compliantContexts: z.number(),
  nonCompliantContexts: z.number(),
  averageComplianceScore: z.number().min(0).max(100),
});
export type MonorepoHealth = z.infer<typeof MonorepoHealthSchema>;

export const DependencyGraphSchema = z.object({
  nodes: z.array(z.object({
    id: z.string(),
    name: z.string(),
    type: z.enum(['core', 'supporting', 'generic']),
    complianceScore: z.number(),
  })),
  edges: z.array(z.object({
    source: z.string(),
    target: z.string(),
    type: z.enum(['depends-on', 'publishes-to', 'subscribes-to']),
  })),
  cycles: z.array(z.array(z.string())),
});
export type DependencyGraph = z.infer<typeof DependencyGraphSchema>;

export const PatternComplianceSchema = z.object({
  modulePatternCompliance: z.number().min(0).max(100),
  symbolUsageCompliance: z.number().min(0).max(100),
  cleanArchitectureCompliance: z.number().min(0).max(100),
  portsAdaptersCompliance: z.number().min(0).max(100),
});
export type PatternCompliance = z.infer<typeof PatternComplianceSchema>;

export const RecommendationSchema = z.object({
  id: z.string(),
  priority: z.enum(['high', 'medium', 'low']),
  category: z.enum(['module-pattern', 'symbol-usage', 'architecture', 'dependencies']),
  title: z.string(),
  description: z.string(),
  affectedPackages: z.array(z.string()),
  estimatedEffort: z.enum(['low', 'medium', 'high']),
});
export type Recommendation = z.infer<typeof RecommendationSchema>;

// Main Entity Schema
export const ArchitecturalAnalysisSchema = z.object({
  id: ArchitecturalAnalysisIdSchema,
  monorepoPath: z.string(),
  analysisType: z.enum(['full', 'incremental', 'targeted']),
  monorepoHealth: MonorepoHealthSchema,
  dependencyGraph: DependencyGraphSchema,
  patternCompliance: PatternComplianceSchema,
  recommendations: z.array(RecommendationSchema),
  analysisStartedAt: z.date(),
  analysisCompletedAt: z.date(),
  analysisDurationMs: z.number(),
  createdAt: z.date(),
});

export type ArchitecturalAnalysisData = z.infer<typeof ArchitecturalAnalysisSchema>;

/**
 * Architectural Analysis Entity
 * 
 * Aggregate root for meta-model analysis of entire monorepo architecture.
 * Provides comprehensive insights into architectural health and compliance.
 */
export class ArchitecturalAnalysis extends Entity<ArchitecturalAnalysisId> {
  private constructor(
    id: ArchitecturalAnalysisId,
    private readonly _monorepoPath: string,
    private readonly _analysisType: 'full' | 'incremental' | 'targeted',
    private _monorepoHealth: MonorepoHealth,
    private _dependencyGraph: DependencyGraph,
    private _patternCompliance: PatternCompliance,
    private _recommendations: Recommendation[],
    private readonly _analysisStartedAt: Date,
    private _analysisCompletedAt: Date,
    private _analysisDurationMs: number,
    private readonly _createdAt: Date
  ) {
    super(id);
  }

  // Factory Methods
  static create(
    monorepoPath: string,
    analysisType: 'full' | 'incremental' | 'targeted'
  ): ArchitecturalAnalysis {
    const id = ArchitecturalAnalysisId.generate();
    const now = new Date();
    
    return new ArchitecturalAnalysis(
      id,
      monorepoPath,
      analysisType,
      {
        totalPackages: 0,
        totalBoundedContexts: 0,
        compliantContexts: 0,
        nonCompliantContexts: 0,
        averageComplianceScore: 0,
      },
      { nodes: [], edges: [], cycles: [] },
      {
        modulePatternCompliance: 0,
        symbolUsageCompliance: 0,
        cleanArchitectureCompliance: 0,
        portsAdaptersCompliance: 0,
      },
      [],
      now,
      now,
      0,
      now
    );
  }

  static fromData(data: ArchitecturalAnalysisData): ArchitecturalAnalysis {
    return new ArchitecturalAnalysis(
      ArchitecturalAnalysisId.fromString(data.id),
      data.monorepoPath,
      data.analysisType,
      data.monorepoHealth,
      data.dependencyGraph,
      data.patternCompliance,
      data.recommendations,
      data.analysisStartedAt,
      data.analysisCompletedAt,
      data.analysisDurationMs,
      data.createdAt
    );
  }

  // Getters
  get monorepoPath(): string { return this._monorepoPath; }
  get analysisType(): 'full' | 'incremental' | 'targeted' { return this._analysisType; }
  get monorepoHealth(): MonorepoHealth { return this._monorepoHealth; }
  get dependencyGraph(): DependencyGraph { return this._dependencyGraph; }
  get patternCompliance(): PatternCompliance { return this._patternCompliance; }
  get recommendations(): Recommendation[] { return this._recommendations; }
  get analysisStartedAt(): Date { return this._analysisStartedAt; }
  get analysisCompletedAt(): Date { return this._analysisCompletedAt; }
  get analysisDurationMs(): number { return this._analysisDurationMs; }
  get createdAt(): Date { return this._createdAt; }

  // Business Logic Methods
  
  /**
   * Complete the analysis with results
   */
  completeAnalysis(
    monorepoHealth: MonorepoHealth,
    dependencyGraph: DependencyGraph,
    patternCompliance: PatternCompliance,
    recommendations: Recommendation[]
  ): void {
    this._monorepoHealth = monorepoHealth;
    this._dependencyGraph = dependencyGraph;
    this._patternCompliance = patternCompliance;
    this._recommendations = recommendations;
    this._analysisCompletedAt = new Date();
    this._analysisDurationMs = this._analysisCompletedAt.getTime() - this._analysisStartedAt.getTime();
  }

  /**
   * Get overall health score
   */
  getOverallHealthScore(): number {
    const scores = [
      this._monorepoHealth.averageComplianceScore,
      this._patternCompliance.modulePatternCompliance,
      this._patternCompliance.symbolUsageCompliance,
      this._patternCompliance.cleanArchitectureCompliance,
      this._patternCompliance.portsAdaptersCompliance,
    ];

    return Math.round(scores.reduce((sum, score) => sum + score, 0) / scores.length);
  }

  /**
   * Get high priority recommendations
   */
  getHighPriorityRecommendations(): Recommendation[] {
    return this._recommendations.filter(rec => rec.priority === 'high');
  }

  /**
   * Get recommendations by category
   */
  getRecommendationsByCategory(category: Recommendation['category']): Recommendation[] {
    return this._recommendations.filter(rec => rec.category === category);
  }

  /**
   * Check if monorepo has circular dependencies
   */
  hasCircularDependencies(): boolean {
    return this._dependencyGraph.cycles.length > 0;
  }

  /**
   * Get packages involved in circular dependencies
   */
  getCircularDependencyPackages(): string[] {
    return [...new Set(this._dependencyGraph.cycles.flat())];
  }

  /**
   * Check if monorepo meets architectural standards
   */
  meetsArchitecturalStandards(): boolean {
    const healthScore = this.getOverallHealthScore();
    const hasNoCycles = !this.hasCircularDependencies();
    const hasGoodCompliance = this._monorepoHealth.averageComplianceScore >= 80;
    
    return healthScore >= 80 && hasNoCycles && hasGoodCompliance;
  }

  /**
   * Get summary for reporting
   */
  getSummary(): {
    overallHealth: number;
    totalPackages: number;
    compliantPackages: number;
    highPriorityIssues: number;
    circularDependencies: number;
    meetsStandards: boolean;
  } {
    return {
      overallHealth: this.getOverallHealthScore(),
      totalPackages: this._monorepoHealth.totalPackages,
      compliantPackages: this._monorepoHealth.compliantContexts,
      highPriorityIssues: this.getHighPriorityRecommendations().length,
      circularDependencies: this._dependencyGraph.cycles.length,
      meetsStandards: this.meetsArchitecturalStandards(),
    };
  }

  /**
   * Convert to plain data object
   */
  toData(): ArchitecturalAnalysisData {
    return {
      id: this.id.toString(),
      monorepoPath: this._monorepoPath,
      analysisType: this._analysisType,
      monorepoHealth: this._monorepoHealth,
      dependencyGraph: this._dependencyGraph,
      patternCompliance: this._patternCompliance,
      recommendations: this._recommendations,
      analysisStartedAt: this._analysisStartedAt,
      analysisCompletedAt: this._analysisCompletedAt,
      analysisDurationMs: this._analysisDurationMs,
      createdAt: this._createdAt,
    };
  }
}